import SendRoundedIcon from "@mui/icons-material/SendRounded"
import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useRef, useState } from "react"

import DialogHeader from "../components/DialogHeader"
import SelectCampaignAutoComplete, {
  useSelectCampaignAutoCompleteState,
} from "../components/SelectCampaignAutoComplete"
import { useDialogContainer } from "../utils"

const TEMPLATES_URL = "/crm/api/concierge-sms/inbox/templates/"

/** Text a number that has no thread yet; replies land in the inbox like any other. */
export default function SmsNewTextDialog({
  open,
  fromPhone,
  isSending,
  onClose,
  onManageTemplates,
  onSend,
}) {
  const Container = useDialogContainer()
  const {
    availableCampaigns,
    setAvailableCampaigns,
    selectedCampaign,
    setSelectedCampaign,
    campaignInputValue,
    setCampaignInputValue,
    campaignsLoaded,
    campaignsLoading,
  } = useSelectCampaignAutoCompleteState(open, TEMPLATES_URL)
  const [to, setTo] = useState("")
  const [draftText, setDraftText] = useState("")
  const appliedTemplateId = useRef(null)

  useEffect(() => {
    if (
      selectedCampaign?.id &&
      appliedTemplateId.current !== selectedCampaign.id
    ) {
      appliedTemplateId.current = selectedCampaign.id
      setDraftText(
        selectedCampaign.rendered_text || selectedCampaign.text || ""
      )
    }
  }, [selectedCampaign])

  function reset() {
    appliedTemplateId.current = null
    setSelectedCampaign(null)
    setCampaignInputValue("")
    setTo("")
    setDraftText("")
  }

  function handleSend() {
    const message = draftText.trim()
    if (!to.trim() || !message) return
    Promise.resolve(onSend(to.trim(), message, selectedCampaign?.id)).then(
      (sent) => {
        if (sent === false) return
        reset()
      }
    )
  }

  const canSend = Boolean(to.trim()) && Boolean(draftText.trim()) && !isSending

  return (
    <Container
      open={open}
      onClose={onClose}
      anchor="right"
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 }, maxWidth: "100%" } }}
    >
      <DialogHeader
        title="New text"
        onClose={onClose}
        actions={
          <Button
            size="small"
            variant="outlined"
            onClick={onManageTemplates}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Manage templates
          </Button>
        }
      />
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 2.5 }}
      >
        {fromPhone ? (
          <Typography variant="body2" sx={{ color: "colors.gray600" }}>
            Sends from {fromPhone}. Replies come back to this inbox.
          </Typography>
        ) : null}
        <TextField
          label="To"
          placeholder="(555) 123-4567"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          disabled={isSending}
          inputProps={{ inputMode: "tel", "aria-label": "To" }}
          size="small"
          fullWidth
          autoFocus
        />
        <SelectCampaignAutoComplete
          compact
          hideHelperText
          size="small"
          availableCampaigns={availableCampaigns}
          setAvailableCampaigns={setAvailableCampaigns}
          campaignInputValue={campaignInputValue}
          setCampaignInputValue={setCampaignInputValue}
          selectedCampaign={selectedCampaign}
          setSelectedCampaign={setSelectedCampaign}
          campaignsLoaded={campaignsLoaded}
          campaignsLoading={campaignsLoading}
          createLinkLabel="Manage templates"
          groupByScope
          loadingLabel="Loading SMS templates..."
          onCreateCampaignLinkClicked={onManageTemplates}
        />
        <TextField
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          placeholder="Type a text message…"
          multiline
          minRows={8}
          maxRows={16}
          disabled={isSending}
          inputProps={{ "aria-label": "Message" }}
          fullWidth
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <LoadingButton
          variant="contained"
          startIcon={<SendRoundedIcon />}
          loading={isSending}
          disabled={!canSend}
          onClick={handleSend}
          sx={{ textTransform: "none", fontWeight: 600, px: 2.5 }}
        >
          Send SMS
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}
